import { createContext, useContext, useMemo, ReactNode, useCallback, useState } from 'react';
import { GetSchedulingResourcesRequest } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/service.pb';
import { V1 as AiSettingsV1 } from '@frontend/api-ai-settings';
import { useGetSchedulingExceptionsQuery } from '../api/availability-exceptions/index';
import { useGetProviders, useGetOperatories, useGetAvailabilityRules } from '../api/availability-rules/index';
import { buildCombinedData, buildEnrichedSchedulingExceptions } from '../api/availability-rules/transformers';
import { useGetServices } from '../api/services/index';
import type { CombinedOperatoriesData, EnrichedScheduleBlockedRule } from '../types/availability-rules.types';
import { validateDatetimeValue } from '../utils/utilities';

export type AvailabilityDataContextRequest = GetSchedulingResourcesRequest & {
  enabled?: boolean;
};

interface AvailabilityDataContextValue {
  // Combined transformed data
  data: CombinedOperatoriesData | undefined;

  // The location ID this context was initialized with
  locationId: string;

  // Combined loading and error states
  isLoading: boolean;
  hasError: boolean;
  error: unknown;

  // Individual raw data for components that need it
  operatoriesData: AiSettingsV1.Types.GetSchedulingResourcesIO['output'] | undefined;
  availabilityRulesData: AiSettingsV1.Types.GetSchedulingSchedulesIO['output'] | undefined;
  servicesData: AiSettingsV1.Types.GetSchedulingAppointmentTypesIO['output'] | undefined;
  providersData: AiSettingsV1.Types.GetSchedulingStaffsIO['output'] | undefined;

  // Individual loading states for granular control
  isFetchingOperatoriesData: boolean;
  isFetchingAvailabilityRulesData: boolean;
  isFetchingServicesData: boolean;
  isFetchingProvidersData: boolean;

  // Individual error states
  operatoriesError: unknown;
  availabilityRulesError: unknown;
  servicesError: unknown;
  providersError: unknown;
  isOperatoriesError: boolean;
  isAvailabilityRulesError: boolean;
  isServicesError: boolean;
  isProvidersError: boolean;

  // Enriched scheduling exceptions (staffIds/serviceIds/resourceIds resolved to {id, name})
  schedulingExceptionsData: EnrichedScheduleBlockedRule[];
  isFetchingSchedulingExceptionsData: boolean;
  schedulingExceptionsError: unknown;
  isSchedulingExceptionsError: boolean;

  // Individual refetch functions
  refetchOperatoriesData: () => void;
  refetchAvailabilityRulesData: () => void;
  refetchServicesData: () => void;
  refetchProvidersData: () => void;
  refetchSchedulingExceptionsData: () => void;
  lastSyncedAt: Date | null;

  // Unpublished changes state
  hasUnpublishedChanges: boolean;
  markAsPublished: () => void;
  setHasUnpublishedChanges: (value: boolean) => void;
}

const AvailabilityDataContext = createContext<AvailabilityDataContextValue | null>(null);

interface AvailabilityDataProviderProps {
  children: ReactNode;
  locationId: string;
  orgId: string;
  enabled?: boolean;
}

export const AvailabilityDataProvider = ({
  children,
  locationId,
  orgId,
  enabled = true,
}: AvailabilityDataProviderProps) => {
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
  const markAsPublished = useCallback(() => setHasUnpublishedChanges(false), []);

  // Fetch operatories data
  const { operatoriesData, isFetchingOperatoriesData, operatoriesError, isOperatoriesError, refetchOperatoriesData } =
    useGetOperatories({
      locationId,
      orgId,
      enabled,
    });

  // Fetch availability rules data
  const {
    availabilityRulesData,
    isFetchingAvailabilityRulesData,
    availabilityRulesError,
    isAvailabilityRulesError,
    refetchAvailabilityRulesData,
  } = useGetAvailabilityRules({
    locationId,
    orgId,
    enabled,
  });

  // Fetch services data (appointment types)
  const { servicesData, isFetchingServicesData, servicesError, isServicesError, refetchServicesData } = useGetServices({
    locationId,
    orgId,
    enabled,
  });

  // Fetch providers data (staff)
  const { providersData, isFetchingProvidersData, providersError, isProvidersError, refetchProvidersData } =
    useGetProviders({
      locationId,
      orgId,
      enabled,
    });

  // Fetch scheduling exceptions (blocked rules)
  const {
    schedulingExceptionsData: rawSchedulingExceptionsData,
    isFetchingSchedulingExceptionsData,
    schedulingExceptionsError,
    isSchedulingExceptionsError,
    refetchSchedulingExceptionsData,
  } = useGetSchedulingExceptionsQuery({
    locationId,
    orgId,
    enabled,
  });

  // Combine the data by matching resourceIds to operatory ids and resolving service/provider names
  const combinedData = useMemo<CombinedOperatoriesData | undefined>(
    () => buildCombinedData(operatoriesData, availabilityRulesData, servicesData, providersData),
    [operatoriesData, availabilityRulesData, servicesData, providersData]
  );

  // Enrich scheduling exceptions with resolved {id, name} objects
  const schedulingExceptionsData = useMemo(
    () =>
      buildEnrichedSchedulingExceptions(
        rawSchedulingExceptionsData?.scheduleBlockedRules,
        servicesData,
        providersData,
        operatoriesData
      ),
    [rawSchedulingExceptionsData, servicesData, providersData, operatoriesData]
  );

  // Combine loading states
  const isLoading =
    isFetchingOperatoriesData ||
    isFetchingAvailabilityRulesData ||
    isFetchingServicesData ||
    isFetchingProvidersData ||
    isFetchingSchedulingExceptionsData;

  // Combine error states
  const hasError =
    isOperatoriesError ||
    isAvailabilityRulesError ||
    isServicesError ||
    isProvidersError ||
    isSchedulingExceptionsError;
  const combinedError =
    operatoriesError || availabilityRulesError || servicesError || providersError || schedulingExceptionsError;

  const contextValue: AvailabilityDataContextValue = {
    // Combined transformed data
    data: combinedData,
    locationId,
    lastSyncedAt: validateDatetimeValue(
      availabilityRulesData?.lastSyncedAt ||
        rawSchedulingExceptionsData?.lastSyncedAt ||
        operatoriesData?.lastSyncedAt ||
        servicesData?.lastSyncedAt ||
        providersData?.lastSyncedAt
    ),
    isLoading,
    hasError,
    error: combinedError,

    // Individual raw data
    operatoriesData,
    availabilityRulesData,
    servicesData,
    providersData,

    // Individual loading states
    isFetchingOperatoriesData,
    isFetchingAvailabilityRulesData,
    isFetchingServicesData,
    isFetchingProvidersData,

    // Individual error states
    operatoriesError,
    availabilityRulesError,
    servicesError,
    providersError,
    isOperatoriesError,
    isAvailabilityRulesError,
    isServicesError,
    isProvidersError,

    refetchOperatoriesData,
    refetchAvailabilityRulesData,
    refetchServicesData,
    refetchProvidersData,

    schedulingExceptionsData,
    isFetchingSchedulingExceptionsData,
    schedulingExceptionsError,
    isSchedulingExceptionsError,
    refetchSchedulingExceptionsData,
    hasUnpublishedChanges,
    setHasUnpublishedChanges,
    markAsPublished,
  };

  return <AvailabilityDataContext.Provider value={contextValue}>{children}</AvailabilityDataContext.Provider>;
};

export const useAvailabilityData = (): AvailabilityDataContextValue => {
  const context = useContext(AvailabilityDataContext);
  if (!context) {
    throw new Error('useAvailabilityData must be used within an AvailabilityDataProvider');
  }
  return context;
};
